import { CircularProgress, makeStyles, Paper } from '@material-ui/core';
import { capitalizeAll, CompCustomActionsMenu, CompCustomActionsMenuProps, SELECT_INPUT_OPTION_DIVIDER, SELECT_INPUT_OPTION_DIVIDER_TYPE,
    TimeSeriesType, useModePanelStyle } from '@moderepo/mode-app-components';
import { modeUIContext, showSlideOutPanel, SlideOutPanelLocation } from '@moderepo/mode-ui-state';
import clsx from 'clsx';
import { useCallback, useContext, useMemo } from 'react';
import { EntityLatestSingleMetricValueUIComponent, EntityLatestSingleMetricValueUIComponentCustomSettings,
    isEntityLatestSingleMetricValueUIComponentCustomSettings } from '../..';
import { EntityUIComponentCollection, ENTITY_DEVICE_ID_ATTRIBUTE, getMetricsDefinitionMetricsId, isEntityUIComponentCustomActionsSettings,
    useSelectTranslatedEntityClasses } from '../../..';
import { RouteHelper } from '../../../..';
import { useRitzTranslation } from '../../../../../utils/customHooks';
import { useSelectEntity, useSelectProject } from '../../../../../utils/dataStateHooks';
import { RitzComponentsCollection } from '../../../../../utils/ritzComponentsCollection';
import { isObjectLiteral, isValidSettingBooleanValue, isValidSettingStringValue } from '../../../../../utils/utils';
import { EntityUIComponentProps } from '../../../EntityDetailsLayouts';
import { DeviceCommandType, DeviceCommandSettings, isPeripheralDeviceCommandSettings } from '../../../../devices/SendDeviceCommandContainer';
import { DevicePreviewCompOptions, SendCommandSlideOutCompOptions, SlideOutPanelCompType } from '../../../../routes/defaultProjectConfig';
import { SettingsErrorLogger } from '../../../../../utils/logger';



const useStyle = makeStyles(() => {
    return {
        root: {
            width: '100%',
        },
    };
}, { name: 'ActlyzerStatusBarUIComponent', index: 1 });



/**
 * This is the interface for the ActlyzerStatusBarUIComponent's CustomSettings.
 */
export interface ActlyzerStatusBarUIComponentCustomSettings extends EntityLatestSingleMetricValueUIComponentCustomSettings {
    // The set of commands to show in the UI for the user to select.
    readonly predefinedCommands?: readonly (SELECT_INPUT_OPTION_DIVIDER_TYPE | DeviceCommandSettings)[] | undefined;

    // Whether or not to disable the custom command option. By default, the component will show the custom action option
    readonly disableCustomCommand?: boolean | undefined;
}


/**
 * Type guard function to validate the obj to make sure it is a type of DeviceCommandSettings
 */
export const isDeviceCommandSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is DeviceCommandSettings => {
    const settings = obj as DeviceCommandSettings;
    const commandTypes = Object.values(DeviceCommandType);
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.type, true, false, commandTypes)) {
        return errorLogger?.logError({
            message: `"type" is required and must be one of the following. [${
                commandTypes.map((type) => {
                    return `"${type}"`;
                }).join(', ')}]`,
            value: settings.type,
            path : [...configPath, 'type'],
        }) ?? false;
    }

    if (settings.type === DeviceCommandType.PERIPHERAL && !isPeripheralDeviceCommandSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    if (!isValidSettingStringValue(settings.command, true)) {
        return errorLogger?.logError({
            message: '"command" is required and must be a non-empty string.',
            value  : settings.command,
            path   : [...configPath, 'command'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.displayNameKey, true)) {
        return errorLogger?.logError({
            message: '"displayNameKey" is required and must be a non-empty string.',
            value  : settings.displayNameKey,
            path   : [...configPath, 'displayNameKey'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.hasParameters)) {
        return errorLogger?.logError({
            message: '"hasParameters" must be undefined or a boolean.',
            value  : settings.hasParameters,
            path   : [...configPath, 'hasParameters'],
        }) ?? false;
    }

    if (settings.defaultParameters !== undefined && !isObjectLiteral(settings.defaultParameters)) {
        return errorLogger?.logError({
            message: '"defaultParameters" must be undefined or an object.',
            value  : settings.defaultParameters,
            path   : [...configPath, 'defaultParameters'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.hasResponse)) {
        return errorLogger?.logError({
            message: '"hasResponse" must be undefined or a boolean.',
            value  : settings.hasResponse,
            path   : [...configPath, 'hasResponse'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.responseActionFieldName)) {
        return errorLogger?.logError({
            message: '"responseActionFieldName" must be undefined or a non-empty string.',
            value  : settings.responseActionFieldName,
            path   : [...configPath, 'responseActionFieldName'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.responseResultFieldName)) {
        return errorLogger?.logError({
            message: '"responseResultFieldName" must be undefined or a non-empty string.',
            value  : settings.responseResultFieldName,
            path   : [...configPath, 'responseResultFieldName'],
        }) ?? false;
    }

    return true;
};



/**
 * Type guard function to validate the obj to make sure it is a type of EntityLatestSingleMetricValueUIComponentCustomSettings
 */
export const isActlyzerStatusBarUIComponentCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is ActlyzerStatusBarUIComponentCustomSettings => {
    const settings = obj as ActlyzerStatusBarUIComponentCustomSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    // predefinedCommands is optional but if it is provided, it must be an array of DeviceCommandSettings
    if (settings.predefinedCommands !== undefined) {
        if (!(settings.predefinedCommands instanceof Array)) {
            return errorLogger?.logError({
                message: '"predefinedCommands" must be undefined or an array of command settings.',
                value  : settings.predefinedCommands,
                path   : [...configPath, 'predefinedCommands'],
            }) ?? false;
        }

        // If some commandSettings in "predefinedCommands" is NOT valid DeviceCommandSettings then return false
        if (!settings.predefinedCommands.every((commandSettings, index) => {
            if (typeof commandSettings === 'string') {
                if (commandSettings !== SELECT_INPUT_OPTION_DIVIDER) {
                    return errorLogger?.logError({
                        message: `Value must be an divider "${SELECT_INPUT_OPTION_DIVIDER}" or an Object`,
                        value  : commandSettings,
                        path   : [...configPath, 'predefinedCommands', `[${index}]`],
                    }) ?? false;
                }
                return true;
            }

            if (!isDeviceCommandSettings(commandSettings, errorLogger, [...configPath, 'predefinedCommands', `[${index}]`])) {
                return false;
            }

            return true;
        })) {
            return false;
        }
    }

    if (!isValidSettingBooleanValue(settings.disableCustomCommand)) {
        return errorLogger?.logError({
            message: '"disableCustomCommand" must be undefined or a boolean.',
            value  : settings.disableCustomCommand,
            path   : [...configPath, 'disableCustomCommand'],
        }) ?? false;
    }

    if (!isEntityLatestSingleMetricValueUIComponentCustomSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    if (!isEntityUIComponentCustomActionsSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    return true;
};



export interface ActlyzerStatusBarUIComponentProps extends EntityUIComponentProps {
    readonly customSettings?: ActlyzerStatusBarUIComponentCustomSettings | undefined;
}


/**
 * This EntityUIComponent is used for showing an entity's 1 single metric value
 */
export const ActlyzerStatusBarUIComponent: React.FC<ActlyzerStatusBarUIComponentProps> = (
    props: ActlyzerStatusBarUIComponentProps,
) => {
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);

    const entityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);
    const selectedEntity = useSelectEntity(props.projectId, props.entityId, false);

    // Get the deviceId from the entity's attribute with key _deviceId_
    const deviceId = useMemo(() => {
        const deviceIdAttrValue = selectedEntity?.attributes?.[ENTITY_DEVICE_ID_ATTRIBUTE];
        if (deviceIdAttrValue !== undefined && typeof deviceIdAttrValue === 'number') {
            return deviceIdAttrValue;
        }
        return undefined;
    }, [selectedEntity?.attributes]);


    /**
     * Find the entity class tied to the selectedEntity
     */
    const selectedEntityClass = useMemo(() => {
        if (selectedEntity && entityClasses) {
            return entityClasses.find((entityClass) => {
                return entityClass.entityClass === selectedEntity.entityClass;
            });
        }
        return undefined;
    }, [entityClasses, selectedEntity]);


    /**
     * Find the metricsDefinition from the entityClass based on the customSettings.metricDefinition
     */
    const selectedMetricsDefinition = useMemo(() => {
        if (selectedEntityClass) {
            return selectedEntityClass.metricsDefinitions?.find((metricDef) => {
                return metricDef.metricsDefinitionId === props.customSettings?.metricsDefinitionId;
            });
        }
        return undefined;
    }, [props.customSettings?.metricsDefinitionId, selectedEntityClass]);


    const classes = useStyle();
    const panelClasses = useModePanelStyle();



    /**
     * On "Show Historical Data" button clicked, we will take the user to the Historical page
     */
    const onShowHistoricalData = useCallback(() => {
        // Get the collectionId for the given metricDefinitionId
        if (selectedEntity && selectedMetricsDefinition) {
            const collectionId = getMetricsDefinitionMetricsId(selectedEntity, selectedMetricsDefinition.metricsDefinitionId);

            RouteHelper.getInstance().gotoHomeHistoricalDataPage({
                projectId        : props.projectId,
                homeId           : selectedEntity.homeId,
                tsdbSmartModuleId: selectedMetricsDefinition.dataStoreConfiguration.moduleInstanceId,
                dataId           : collectionId,
                timeSeriesType   : TimeSeriesType.COLLECTION,
                metricsDefinition: {
                    entityClass        : selectedEntity.entityClass,
                    metricsDefinitionId: selectedMetricsDefinition.metricsDefinitionId,
                },
            });
        }
    }, [props, selectedEntity, selectedMetricsDefinition]);



    /**
     * On "Send Command to Device" button clicked, we will open the slide out panel for Send Device Command component
     */
    const onSendCommandToDevice = useCallback(() => {
        if (deviceId) {
            const options: SendCommandSlideOutCompOptions = {
                location : SlideOutPanelLocation.RIGHT,
                compType : SlideOutPanelCompType.SEND_COMMAND_TO_DEVICE,
                compProps: {
                    data: {
                        projectId           : props.projectId,
                        deviceId,
                        predefinedCommands  : props.customSettings?.predefinedCommands,
                        disableCustomCommand: props.customSettings?.disableCustomCommand,
                    },
                },
            };
            uiDispatch(showSlideOutPanel(options));
        }
    }, [deviceId, props.customSettings?.disableCustomCommand, props.customSettings?.predefinedCommands, props.projectId, uiDispatch]);



    /**
     * On "Show Device Info" button clicked, we will open the preview panel to show the device info
     */
    const onShowDeviceInfo = useCallback(() => {
        if (deviceId) {
            const options: DevicePreviewCompOptions = {
                location : SlideOutPanelLocation.RIGHT,
                compType : SlideOutPanelCompType.DEVICE_PREVIEW,
                compProps: {
                    data: {
                        projectId: props.projectId,
                        deviceId,
                    },
                },
            };
            uiDispatch(showSlideOutPanel(options));
        }
    }, [props.projectId, deviceId, uiDispatch]);



    /**
     * Create and cache the set of actions to be shown in the menu
     */
    const actions = useMemo((): CompCustomActionsMenuProps['customActions'] => {

        // Create an Array of actions that are only available if there is a deviceId. Create [] if there is no deviceId.
        const deviceActions: CompCustomActionsMenuProps['customActions'] = deviceId
            ? [{
                label  : capitalizeAll(trans('label.send_command_to_device')),
                icon   : 'send_to_mobile',
                onClick: onSendCommandToDevice,
            }, {
                label  : capitalizeAll(trans('label.device_info')),
                icon   : 'info',
                onClick: onShowDeviceInfo,
            }]
            : [];

        // Return the "View Chart" action which doesn't require deviceId and the deviceActions
        return [
            {
                label  : capitalizeAll(trans('label.show_chart_view')),
                icon   : 'show_charts',
                onClick: onShowHistoricalData,
            },
            ...deviceActions,
        ];
    }, [deviceId, onSendCommandToDevice, onShowDeviceInfo, onShowHistoricalData, trans]);


    return (
        <>
            {selectedEntity && (
                <Paper elevation={2} className={clsx(props.className, classes.root)}>
                    <div className={clsx(panelClasses.panelHeader, 'borderless')}>
                        <div className={panelClasses.panelHeaderLeft}>
                            <EntityLatestSingleMetricValueUIComponent
                                projectId={props.projectId}
                                entityId={props.entityId}
                                customSettings={props.customSettings}
                                fetchDataParams={props.fetchDataParams}
                            />
                        </div>
                        <div className={panelClasses.panelHeaderRight}>
                            <CompCustomActionsMenu customActions={actions} />
                        </div>
                    </div>
                </Paper>
            )}

            {!selectedEntity && (
                <CircularProgress />
            )}
        </>
    );
};


// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent(
    'ActlyzerStatusBarUIComponent', ActlyzerStatusBarUIComponent,
);



// Register the component with the EntityUIComponentCollection so that the component can be used by the Entity Layout System
EntityUIComponentCollection.getInstance().registerComponent(
    'Actlyzer_Status_Bar_Comp',
    {
        componentFunc          : ActlyzerStatusBarUIComponent,
        customSettingsValidator: isActlyzerStatusBarUIComponentCustomSettings,
    },
);
